import { LEAVE_TYPE_REGISTRY, type LeaveTypeOperationalKey } from './leave-type-registry';

/**
 * A leave type is requestable only when a live tenant-policy read proves it.
 * Registry capability alone does not establish an approved provider policy.
 * Missing/failed reads remain unavailable; they never imply entitlement.
 */

/**
 * TS mirror of `internal.leave_priv_submit_resolve_operational_key`
 * (20271031000014 L4059-4105) restricted to the tokens the forms submit.
 * PERSONAL resolves per award at submit time, so it is offered when EITHER
 * cohort key is ready.
 */
export const LEAVE_SUBMIT_TOKEN_OPERATIONAL_KEYS = Object.freeze({
  ANNUAL: ['ANNUAL'],
  PERSONAL: ['PERSONAL_GENERAL_STAFF', 'PERSONAL_NURSES'],
  CARERS_EXTRA_GENERAL_STAFF: ['CARERS_EXTRA_GENERAL_STAFF'],
  LONG_SERVICE: ['LONG_SERVICE'],
  UNPAID: ['UNPAID'],
  COMPASSIONATE: ['COMPASSIONATE'],
  PARENTAL: ['PARENTAL'],
  COMMUNITY_SERVICE: ['COMMUNITY_SERVICE'],
  WORKERS_COMP_NOT_WORKED: ['WORKERS_COMP_NOT_WORKED'],
  TIME_IN_LIEU_TAKEN: ['TOIL'],
  ACCRUED_DAY_OFF_TAKEN: ['ADO'],
} as const satisfies Record<string, readonly LeaveTypeOperationalKey[]>);

export type LeaveSubmitToken = keyof typeof LEAVE_SUBMIT_TOKEN_OPERATIONAL_KEYS;

/** Retained API: no type is enabled without tenant-policy evidence. */
export const CONSERVATIVE_STATIC_OFFERED_LEAVE_SUBMIT_TOKENS: readonly LeaveSubmitToken[] =
  Object.freeze([]);

export interface LeaveTypePolicyReadinessKey {
  readonly ready: boolean;
  /** Registry held reason or the finalize's hold class; null when ready. */
  readonly holdReason: string | null;
}

export interface LeaveTypePolicyReadiness {
  readonly registrySchemaVersion: string | null;
  readonly atDate: string;
  readonly keys: Readonly<Record<string, LeaveTypePolicyReadinessKey>>;
}

let staticHeldCache: ReadonlySet<LeaveTypeOperationalKey> | null = null;

/**
 * Registry 1.2.0 had to make these definitions policy-capable: the database
 * policy validator will not allow a tenant policy to elevate a HELD/NEVER
 * definition. They are nevertheless tenant opt-ins, not global defaults.
 * Without a successful tenant-policy readiness read the UI must therefore
 * keep both types held. Medea's APPROVED policies reveal them through the live
 * readiness branch; another tenant can never inherit Medea's enablement from
 * this process-wide registry mirror.
 */
const STATIC_TENANT_POLICY_PROOF_REQUIRED_KEYS: ReadonlySet<LeaveTypeOperationalKey> = new Set([
  'LONG_SERVICE',
  'WORKERS_COMP_NOT_WORKED',
]);

/**
 * Registry keys held by the static no-read fallback. This includes tenant-policy-
 * proof-required keys, registry rows with a non-null heldReason code, and
 * NEVER-requestability types. Requestability HELD alone is not evaluated here.
 */
export function staticRegistryHeldOperationalKeys(): ReadonlySet<LeaveTypeOperationalKey> {
  if (staticHeldCache) return staticHeldCache;
  const held = new Set<LeaveTypeOperationalKey>();
  for (const row of Object.values(LEAVE_TYPE_REGISTRY)) {
    if (
      STATIC_TENANT_POLICY_PROOF_REQUIRED_KEYS.has(row.operationalKey) ||
      row.heldReason !== null ||
      row.requestability === 'NEVER'
    ) {
      held.add(row.operationalKey);
    }
  }
  staticHeldCache = held;
  return held;
}

export function operationalKeysForLeaveSubmitToken(
  token: string,
): readonly LeaveTypeOperationalKey[] {
  const normalized = token.trim().toUpperCase();
  return (
    (LEAVE_SUBMIT_TOKEN_OPERATIONAL_KEYS as Record<string, readonly LeaveTypeOperationalKey[]>)[
      normalized
    ] ?? []
  );
}

/**
 * Whether a request form may offer `token`. Fails closed: a token with no
 * known registry key is never offered (the SQL resolver would raise
 * `leave_priv_submit_spine_type_unprovable` for it anyway).
 */
export function isLeaveSubmitTypeOffered(
  token: string,
  readiness: LeaveTypePolicyReadiness | null,
): boolean {
  const keys = operationalKeysForLeaveSubmitToken(token);
  if (keys.length === 0) return false;
  if (readiness) {
    return keys.some((key) => readiness.keys[key]?.ready === true);
  }
  const normalized = token.trim().toUpperCase();
  return (CONSERVATIVE_STATIC_OFFERED_LEAVE_SUBMIT_TOKENS as readonly string[]).includes(
    normalized,
  );
}

export function filterOfferedLeaveSubmitTypes<T extends string>(
  tokens: readonly T[],
  readiness: LeaveTypePolicyReadiness | null,
): T[] {
  return tokens.filter((token) => isLeaveSubmitTypeOffered(token, readiness));
}

/** Worker-facing reason a hidden type is unavailable (never a raw token). */
export function leaveSubmitTypeHoldExplanation(
  token: string,
  readiness: LeaveTypePolicyReadiness | null,
): string | null {
  if (isLeaveSubmitTypeOffered(token, readiness)) return null;
  if (!readiness) return 'Leave policy availability could not be checked. Retry before submitting.';
  return 'This leave type is not set up for your organisation yet — ask your coordinator.';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Strict parser for the 056 RPC payload. Anything malformed yields null so
 * the caller takes the static fallback instead of trusting a partial read.
 */
export function parseLeaveTypePolicyReadiness(input: unknown): LeaveTypePolicyReadiness | null {
  if (!isRecord(input)) return null;
  const atDate = input.at_date;
  const registry = input.registry_schema_version;
  const rawKeys = input.keys;
  if (typeof atDate !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(atDate)) return null;
  if (registry !== null && typeof registry !== 'string') return null;
  if (!Array.isArray(rawKeys)) return null;
  const keys: Record<string, LeaveTypePolicyReadinessKey> = {};
  for (const entry of rawKeys) {
    if (!isRecord(entry)) return null;
    const key = entry.operational_key;
    const ready = entry.ready;
    const holdReason = entry.hold_reason;
    if (typeof key !== 'string' || key.length === 0) return null;
    if (typeof ready !== 'boolean') return null;
    if (holdReason !== null && typeof holdReason !== 'string') return null;
    // A duplicated key would let a trailing `ready:true` row flip an earlier
    // hold (last write wins) — treat the payload as malformed instead.
    if (key in keys) return null;
    keys[key] = { ready, holdReason: holdReason as string | null };
  }
  return { registrySchemaVersion: registry as string | null, atDate, keys };
}
